import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Create axios instance with auth
const api = axios.create({
  baseURL: `${API_BASE}/api/qd`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('qd_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('qd_token');
      localStorage.removeItem('qd_user');
      window.location.href = '/quote/login';
    }
    return Promise.reject(error);
  }
);

// Helper to format money
export const formatMoney = (amount) => {
  return new Intl.NumberFormat('lt-LT', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount || 0);
};

// Helper to format date
export const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('lt-LT');
};

// Helper to get API error message
export const getApiError = (error) => {
  return error.response?.data?.detail || error.message || 'Įvyko klaida';
};

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const getMe = () => api.get('/auth/me');
export const changePassword = (current_password, new_password) => 
  api.post('/auth/change-password', { current_password, new_password });

// Users
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.patch(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const resetUserPassword = (id) => api.post(`/users/${id}/reset-password`);
export const sendUserCredentials = (id) => api.post(`/users/${id}/send-credentials`);

// Clients
export const getClients = () => api.get('/clients');
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.patch(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);

// Products
export const getProducts = () => api.get('/products');
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.patch(`/products/${id}`, data);

// Quotes
export const getQuotes = (status) => api.get('/quotes', { params: { status } });
export const getQuote = (id) => api.get(`/quotes/${id}`);
export const createQuote = (data) => api.post('/quotes', data);
export const updateQuote = (id, data) => api.patch(`/quotes/${id}`, data);
export const deleteQuote = (id) => api.delete(`/quotes/${id}`);
export const duplicateQuote = (id) => api.post(`/quotes/${id}/duplicate`);
export const sendQuote = (id, data) => api.post(`/quotes/${id}/send`, data);
export const getQuotePdfUrl = (id) => `${API_BASE}/api/qd/quotes/${id}/pdf`;
export const exportQuotesExcel = (params) => api.get('/quotes/export', { 
  params,
  responseType: 'blob' 
});

// Templates
export const getTemplates = () => api.get('/templates');
export const getTemplate = (id) => api.get(`/templates/${id}`);
export const createTemplate = (data) => api.post('/templates', data);
export const updateTemplate = (id, data) => api.patch(`/templates/${id}`, data);
export const deleteTemplate = (id) => api.delete(`/templates/${id}`);

// Reminders
export const getExpiringQuotes = () => api.get('/reminders/expiring');
export const checkExpirations = () => api.post('/reminders/check-expirations');

// Attachments
export const getAttachments = (quoteId) => api.get(`/quotes/${quoteId}/attachments`);
export const uploadAttachment = (quoteId, file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/quotes/${quoteId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress
  });
};
export const deleteAttachment = (quoteId, attachmentId) => 
  api.delete(`/quotes/${quoteId}/attachments/${attachmentId}`);

// Documents
export const getDocuments = () => api.get('/documents');
export const createDocument = (data) => api.post('/documents', data);
export const updateDocument = (id, data) => api.patch(`/documents/${id}`, data);

// Entities
export const getEntities = () => api.get('/entities');

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

export default api;
